import type { CallbackEvent } from "@shopify/polaris-types";
import { stripShopifyGid } from "@codway-dev/shared";
import type { ShopCurrencyContext } from "../../../hooks/useShopCurrency";
import type { OneTickEditorState } from "../../../store/one-tick-editor.mapping";
import {
  CollapsibleSectionCard,
  SectionHeader,
  Divider,
  ColorRow,
  NumericField,
  ProductPickerButton,
} from "../../../components/shared/EditorShared";

// ─── Types ───────────────────────────────────────────────────────────────────

export type OneTickBorderStyle = "solid" | "dashed" | "dotted" | "none";

/**
 * Subset of the Shopify product fields the 1-tick editor cares about.
 * 1-tick has no variant selector (unlike the 1-click modal) — the
 * merchant only picks the product. We still capture `defaultVariantId`
 * (the product's first variant) because the order pipeline requires a
 * variant ID to build a Shopify line item; without it the BE has no
 * variant to attach the upsell discount to and the order fails with
 * "variant not found, price must be provided".
 */
export interface OneTickSelectedProduct {
  id: string;
  title: string;
  price: string;
  imageUrl?: string;
  description?: string;
  defaultVariantId?: string;
}

export type OneTickDesignValues = Pick<
  OneTickEditorState,
  | "upsellTitle"
  | "upsellPrice"
  | "checkboxTemplate"
  | "description"
  | "textColor"
  | "descriptionColor"
  | "linkedProductEnabled"
  | "selectedProduct"
  | "imageUrl"
  | "backgroundColor"
  | "borderStyle"
  | "borderWidth"
  | "borderColor"
  | "borderRadius"
>;

export type OneTickDesignChange = <K extends keyof OneTickDesignValues>(
  key: K,
  value: OneTickDesignValues[K]
) => void;

// ─── Picker helper ───────────────────────────────────────────────────────────

async function openProductPicker(
  onSelect: (product: OneTickSelectedProduct) => void
) {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: false,
    });
    if (!selected || selected.length === 0) return;
    const p = selected[0];
    const defaultVariant = p.variants[0];
    onSelect({
      id: stripShopifyGid(p.id),
      title: p.title,
      price: defaultVariant?.price ?? "0.00",
      imageUrl: p.images[0]?.originalSrc,
      description: p.description,
      defaultVariantId: defaultVariant
        ? stripShopifyGid(defaultVariant.id)
        : undefined,
    });
  } catch {
    window.shopify?.toast?.show("No se pudo abrir el selector de productos.", {
      isError: true,
    });
  }
}

// ─── Template helper (1-tick specific codes) ─────────────────────────────────

function TemplateCodes() {
  return (
    <s-stack direction="block" gap="small-100">
      <s-text color="subdued">
        Use {"{title}"} y {"{price}"} para insertar el título y el precio del
        upsell dentro de este texto.
      </s-text>
    </s-stack>
  );
}

// ─── Section: Personaliza el upsell ──────────────────────────────────────────

export function DesignSection({
  values,
  onDesignChange,
  money,
}: {
  values: OneTickDesignValues;
  onDesignChange: OneTickDesignChange;
  /** Shop currency + locale-aware money formatter. */
  money: ShopCurrencyContext;
}) {
  const { currency, formatPrice } = money;
  const {
    upsellTitle,
    upsellPrice,
    checkboxTemplate,
    description,
    textColor,
    descriptionColor,
    linkedProductEnabled,
    selectedProduct,
    imageUrl,
    backgroundColor,
    borderStyle,
    borderWidth,
    borderColor,
    borderRadius,
  } = values;

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={2} title="Personaliza el upsell" />}
    >
      <s-stack direction="block" gap="base">
        {/* Title + Price (two columns) */}
        <div className="grid grid-cols-2 gap-3">
          <s-text-field
            label="Título del upsell"
            value={upsellTitle}
            onInput={(e: CallbackEvent<"s-text-field">) =>
              onDesignChange("upsellTitle", e.currentTarget.value)
            }
          />
          <NumericField
            label={`Precio del upsell (${currency})`}
            min={0}
            value={upsellPrice}
            onChange={(v) => onDesignChange("upsellPrice", v)}
          />
        </div>

        {/* Checkbox text template */}
        <s-stack direction="block" gap="small-200">
          <s-text-field
            label="Texto de la casilla de verificación"
            value={checkboxTemplate}
            onInput={(e: CallbackEvent<"s-text-field">) =>
              onDesignChange("checkboxTemplate", e.currentTarget.value)
            }
          />
          <TemplateCodes />
        </s-stack>

        {/* Description */}
        <s-text-field
          label="Texto de descripción"
          value={description}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onDesignChange("description", e.currentTarget.value)
          }
        />

        {/* Text colors (two columns) */}
        <div className="grid grid-cols-2 gap-3">
          <ColorRow
            label="Color del texto"
            value={textColor}
            onChange={(v) => onDesignChange("textColor", v)}
          />
          <ColorRow
            label="Descripción color"
            value={descriptionColor}
            onChange={(v) => onDesignChange("descriptionColor", v)}
          />
        </div>

        <Divider />

        {/* Linked product toggle */}
        <s-stack direction="block" gap="small-200">
          <s-checkbox
            label="Conecte el upsell a un producto en tienda"
            checked={linkedProductEnabled}
            onChange={(e: CallbackEvent<"s-checkbox">) =>
              onDesignChange("linkedProductEnabled", e.currentTarget.checked)
            }
          />
          <s-text color="subdued">
            El producto se agregará a tus pedidos si el cliente selecciona el
            upsell.
          </s-text>

          {linkedProductEnabled &&
            (selectedProduct ? (
              <s-stack direction="block" gap="small-200">
                {/* Top action: change the linked product. Mirrors Releasit's
                    placement of the swap action above the product card. */}
                <button
                  onClick={() =>
                    openProductPicker((p) =>
                      onDesignChange("selectedProduct", p)
                    )
                  }
                  className="self-start px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                >
                  Cambiar producto
                </button>

                <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                  {selectedProduct.imageUrl ? (
                    <img
                      src={selectedProduct.imageUrl}
                      alt={selectedProduct.title}
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <s-icon type="image" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedProduct.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ID del producto:{" "}
                      <span className="font-medium text-gray-700">
                        {selectedProduct.id}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Precio del producto:{" "}
                      <span className="font-medium text-gray-700">
                        {formatPrice(selectedProduct.price)}
                      </span>
                    </p>
                    {(() => {
                      // Mirrors `getDiscountBadgeLabel` for `fixed_price_per_item`:
                      // only surface the discount line when there are real savings.
                      const productPrice = Number(selectedProduct.price);
                      const savings = productPrice - upsellPrice;
                      if (!Number.isFinite(savings) || savings <= 0)
                        return null;
                      return (
                        <p className="text-xs text-gray-500">
                          Descuento aplicado al producto:{" "}
                          <span className="font-medium text-gray-700">
                            {formatPrice(savings)}
                          </span>
                        </p>
                      );
                    })()}
                  </div>

                  <button
                    onClick={() => onDesignChange("selectedProduct", null)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                    aria-label="Quitar producto"
                  >
                    <s-icon type="delete" />
                  </button>
                </div>

                <s-banner tone="warning">
                  <s-text>
                    Si el precio del producto que está conectando es diferente
                    al precio que configuró en este upsell, se aplica
                    automáticamente un descuento al precio del producto.
                  </s-text>
                </s-banner>
              </s-stack>
            ) : (
              <ProductPickerButton
                label="Seleccionar producto"
                onClick={() =>
                  openProductPicker((p) => onDesignChange("selectedProduct", p))
                }
              />
            ))}
        </s-stack>

        <Divider />

        {/* Image URL */}
        <s-stack direction="block" gap="small-200">
          <s-text-field
            label="URL de la imagen"
            value={imageUrl}
            onInput={(e: CallbackEvent<"s-text-field">) =>
              onDesignChange("imageUrl", e.currentTarget.value)
            }
          />
          <s-link>Cómo agregar una imagen a tu upsell</s-link>
        </s-stack>

        <Divider />

        {/* Background + Border style (two columns) */}
        <div className="grid grid-cols-2 gap-3 items-end">
          <ColorRow
            label="Color del fondo"
            value={backgroundColor}
            onChange={(v) => onDesignChange("backgroundColor", v)}
          />
          <s-select
            label="Estilo de borde"
            value={borderStyle}
            onChange={(e: CallbackEvent<"s-select">) =>
              onDesignChange(
                "borderStyle",
                e.currentTarget.value as OneTickBorderStyle
              )
            }
          >
            <s-option value="solid">Solid</s-option>
            <s-option value="dashed">Dashed</s-option>
            <s-option value="dotted">Dotted</s-option>
            <s-option value="none">None</s-option>
          </s-select>
        </div>

        {/* Border width slider + Border color (two columns) */}
        <div className="grid grid-cols-2 gap-3 items-end">
          <s-stack direction="block" gap="small-200">
            <s-text>Ancho del borde</s-text>
            <input
              type="range"
              min={0}
              max={10}
              value={borderWidth}
              onChange={(e) =>
                onDesignChange("borderWidth", Number(e.target.value))
              }
              className="w-full accent-indigo-600"
            />
          </s-stack>
          <ColorRow
            label="Color del borde"
            value={borderColor}
            onChange={(v) => onDesignChange("borderColor", v)}
          />
        </div>

        {/* Border radius slider */}
        <s-stack direction="block" gap="small-200">
          <s-text>Radio del borde</s-text>
          <input
            type="range"
            min={0}
            max={50}
            value={borderRadius}
            onChange={(e) =>
              onDesignChange("borderRadius", Number(e.target.value))
            }
            className="w-full accent-indigo-600"
          />
        </s-stack>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
